// src/components/PhoneAuth/otpChannels.js
//
// Every sentence the customer reads about HOW a verification code travelled
// lives here, so the phrasing can never drift between the send toast, the OTP
// screen, the fallback notice and the error banner.
//
// THE RULE THIS FILE EXISTS TO ENFORCE: copy is written from the channel the
// server says it ACTUALLY used (`channel` in the send-OTP response), never from
// the channel we asked for. The backend falls back deliberately — a WhatsApp
// request can come back as SMS, an SMS request can come back as email — and a
// screen that insists "check your SMS" while the code is sitting in an inbox is
// how a working failover still reads as a broken product.

export const OTP_CHANNEL = {
  SMS: "sms",
  WHATSAPP: "whatsapp",
  EMAIL: "email",
  AUTO: "auto",
};

const DELIVERABLE = [OTP_CHANNEL.SMS, OTP_CHANNEL.WHATSAPP, OTP_CHANNEL.EMAIL];

// Sentence-initial / standalone name.
const TITLE = {
  [OTP_CHANNEL.SMS]: "SMS",
  [OTP_CHANNEL.WHATSAPP]: "WhatsApp",
  [OTP_CHANNEL.EMAIL]: "Email",
};

// Mid-sentence prepositional phrase — "sent ___ to +256 …".
// WhatsApp reads "on", the others read "by"; getting this wrong is the kind of
// small wrongness that makes a security screen feel machine-generated.
const PHRASE = {
  [OTP_CHANNEL.SMS]: "by SMS",
  [OTP_CHANNEL.WHATSAPP]: "on WhatsApp",
  [OTP_CHANNEL.EMAIL]: "by email",
};

/**
 * Coerce whatever the server sent into one of our known channels.
 * Returns null for anything unrecognised so callers degrade to neutral copy
 * ("Code sent to …") instead of asserting a channel we cannot vouch for.
 */
export const normalizeChannel = (value) => {
  if (typeof value !== "string") return null;
  const v = value.trim().toLowerCase().replace(/[\s_-]/g, "");
  if (v === "whatsapp") return OTP_CHANNEL.WHATSAPP;
  if (v === "sms" || v === "text") return OTP_CHANNEL.SMS;
  if (v === "email" || v === "mail") return OTP_CHANNEL.EMAIL;
  return null;
};

export const channelTitle = (channel) => TITLE[channel] || "";

export const channelPhrase = (channel) => PHRASE[channel] || "";

export const isDeliverableChannel = (channel) => DELIVERABLE.includes(channel);

/**
 * The confirmation line on the OTP screen, split so the destination can be
 * emphasised by the caller.
 *   sentCopy("whatsapp", "+256 78 ··· 0102")
 *     -> { prefix: "Code sent on WhatsApp to", destination: "+256 78 ··· 0102" }
 */
export const sentCopy = (channel, destination) => {
  const phrase = channelPhrase(channel);
  return {
    prefix: phrase ? `Code sent ${phrase} to` : "Code sent to",
    destination: destination || "",
  };
};

/**
 * Said out loud when the customer asked for one channel and the server used
 * another. Silence here is the failure mode: someone who picked WhatsApp and
 * sits staring at WhatsApp while the code is in their inbox will conclude the
 * product is broken and leave.
 */
export const fallbackNotice = (requested, actual) => {
  if (!requested || !actual) return null;
  if (requested === OTP_CHANNEL.AUTO) return null;
  if (requested === actual) return null;
  if (!isDeliverableChannel(actual)) return null;
  return `${channelTitle(requested)} wasn't available, so we sent your code ${channelPhrase(
    actual
  )} instead.`;
};

/** Banner heading when a send failed outright on the requested channel. */
export const channelUnavailableTitle = (channel) =>
  isDeliverableChannel(channel)
    ? `${channelTitle(channel)} isn't available right now`
    : "Verification is temporarily unavailable";

/** The channel we offer as a one-tap escape when `channel` just failed. */
export const alternateChannel = (channel) =>
  channel === OTP_CHANNEL.WHATSAPP ? OTP_CHANNEL.SMS : OTP_CHANNEL.WHATSAPP;

/**
 * The channels a customer can pick BEFORE we have any address for them — both
 * reach a phone number, so both are offered up-front. WhatsApp is listed
 * first because it is the pre-selected default (see PhoneAuth), and the
 * default should be the first thing the eye lands on.
 * Email is not here on purpose: it needs an address we have to ask for, so it
 * appears once there is somewhere to send it.
 */
export const REQUESTABLE_CHANNELS = [
  {
    value: OTP_CHANNEL.WHATSAPP,
    label: "WhatsApp",
    hint: "Arrives in your WhatsApp chat",
  },
  {
    value: OTP_CHANNEL.SMS,
    label: "SMS",
    hint: "Standard text message",
  },
];
